<template>
    <tool-panel ref="panel" @close="$emit('close')">
        <template #header>
            <tool-panel-header
                :title="$t('ai_chat.title')"
                :fullscreen="$refs.panel?.isFullscreen"
                @toggle-fullscreen="$refs.panel.toggleFullscreen()"
                @close="$refs.panel.close()"
            />
        </template>
        <tool-panel-tabs ref="tabs" :tabs="chatTabs">
            <template #actions>
                <font-awesome-icon
                    icon="plus"
                    class="action-icon"
                    tabindex="0"
                    role="button"
                    :title="$t('ai_chat.new_conversation')"
                    @click="newConversation"
                    @keydown.enter="newConversation"
                />
            </template>
            <template #tab-chat>
                <div ref="messagesContainer" class="chat-messages">
                    <div v-for="(message, index) in messages" :key="index" class="message" :class="message.role">
                        <div class="message-content markdown-body">
                            <div v-html="renderMarkdown(message.content)" />
                            <sub v-if="message.role === 'assistant' && message.language && message.language !== 'fi'" class="translation-disclaimer"><font-awesome-icon icon="globe" class="disclaimer-icon" /> {{ $t('ai_chat.translation_disclaimer') }}</sub>
                        </div>
                    </div>
                    <div v-if="isTyping" class="message assistant">
                        <div class="message-content typing-skeleton">
                            <span class="placeholder-glow"><span class="placeholder col-8" /></span>
                            <span class="placeholder-glow"><span class="placeholder col-5" /></span>
                        </div>
                    </div>
                    <div v-if="showFaq" class="faq-buttons">
                        <button v-for="key in faqKeys" :key="key" class="faq-button" @click="sendFaq(key)">
                            {{ $t('ai_chat.' + key) }}
                        </button>
                    </div>
                </div>
                <div class="chat-input">
                    <input
                        v-model="inputMessage"
                        type="text"
                        maxlength="4000"
                        :aria-label="$t('ai_chat.placeholder')"
                        :placeholder="$t('ai_chat.placeholder')"
                        :disabled="isTyping"
                        @keyup.enter="sendMessage"
                    >
                    <button class="send-button" :aria-label="$t('ai_chat.send')" :disabled="isTyping || !inputMessage.trim()" @click="sendMessage">
                        <font-awesome-icon icon="paper-plane" />
                    </button>
                </div>
            </template>
            <template #tab-history>
                <div class="sidebar-list">
                    <div v-if="loadingConversations" class="sidebar-empty">
                        <font-awesome-icon icon="spinner" spin />
                    </div>
                    <div v-else-if="conversations.length === 0" class="sidebar-empty">
                        {{ $t('ai_chat.no_conversations') }}
                    </div>
                    <div
                        v-for="conv in conversations"
                        :key="conv.id"
                        class="sidebar-item"
                        :class="{ active: conv.id === conversationId }"
                        @click="loadConversation(conv.id)"
                    >
                        <div class="sidebar-item-title">{{ conv.title }}</div>
                        <div class="sidebar-item-meta">
                            {{ formatDate(conv.updated_at) }}
                            <font-awesome-icon icon="trash" class="delete-icon" role="button" :aria-label="$t('ai_chat.delete_conversation')" @click.stop="deleteConversation(conv.id)" />
                        </div>
                    </div>
                </div>
            </template>
        </tool-panel-tabs>
    </tool-panel>
</template>

<script>
import { restApi } from '../mixins/RestApiMixin'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import ToolPanel from '../toolbar/ToolPanel.vue'
import ToolPanelHeader from '../toolbar/ToolPanelHeader.vue'
import ToolPanelTabs from '../toolbar/ToolPanelTabs.vue'

export default {
    name: 'AiChatPanel',
    components: { ToolPanel, ToolPanelHeader, ToolPanelTabs },
    mixins: [restApi],
    emits: ['close'],
    data() {
        return {
            inputMessage: '',
            messages: [],
            conversations: [],
            conversationId: null,
            isTyping: false,
            loadingConversations: false,
            faqKeys: ['faq_1', 'faq_2', 'faq_3', 'faq_4']
        }
    },
    computed: {
        chatTabs() {
            return [
                { key: 'chat', label: this.$t('ai_chat.tab_chat') },
                { key: 'history', label: this.$t('ai_chat.tab_history') }
            ]
        },
        showFaq() {
            return !this.conversationId && !this.isTyping && this.messages.length === 1 && this.messages[0].role === 'assistant'
        }
    },
    mounted() {
        this.fetchConversations(false, () => {
            if (this.conversations.length > 0) this.loadConversation(this.conversations[0].id)
            else this.addBotMessage(this.$t('ai_chat.welcome'))
        })
    },
    methods: {
        newConversation() {
            this.conversationId = null
            this.messages = []
            this.addBotMessage(this.$t('ai_chat.welcome'))
            if (this.$refs.tabs) this.$refs.tabs.activeTab = 'chat'
        },
        sendFaq(key) {
            this.inputMessage = this.$t('ai_chat.' + key)
            this.sendMessage()
        },
        sendMessage() {
            const text = this.inputMessage.trim()
            if (!text || this.isTyping) return
            const locale = this.$i18n?.locale || 'fi'
            this.messages.push({ role: 'user', content: text, language: locale })
            this.inputMessage = ''
            this.scrollToBottom()
            this.isTyping = true
            this.restAdd(
                this.chatUrl,
                { message: text, language: locale, conversationId: this.conversationId },
                (data) => {
                    this.isTyping = false
                    const isNew = !this.conversationId
                    this.conversationId = data.conversationId
                    this.addBotMessage(data.response, locale)
                    this.scrollToBottom()
                    if (isNew) this.fetchConversations(false)
                },
                (error) => {
                    this.isTyping = false
                    const status = error?.status
                    const errorLocale = this.$i18n?.locale || 'fi'
                    if (status === 403) this.addBotMessage(this.$t('ai_chat.no_subscription'), errorLocale)
                    else if (status === 404) { this.addBotMessage(this.$t('ai_chat.conversation_not_found'), errorLocale); this.conversationId = null }
                    else if (status === 429) this.addBotMessage(this.$t('ai_chat.token_limit_reached'), errorLocale)
                    else this.addBotMessage(this.$t('ai_chat.error'), errorLocale)
                    this.scrollToBottom()
                }
            )
        },
        fetchConversations(showLoading = true, onDone = null) {
            if (showLoading) this.loadingConversations = true
            this.restFetch(this.chatConversationsUrl, (response) => {
                this.loadingConversations = false
                if (response?.data) this.conversations = response.data
                if (onDone) onDone()
            }, () => { this.loadingConversations = false; this.conversations = []; if (onDone) onDone() })
        },
        loadConversation(id) {
            if (!id) return
            this.restFetch(this.chatConversationUrl + '/' + encodeURIComponent(id), (response) => {
                if (response?.data?.history?.length) {
                    this.conversationId = response.data.id
                    this.messages = response.data.history.map(msg => ({
                        role: msg.role === 'user' ? 'user' : 'assistant',
                        content: msg.content,
                        language: msg.language || 'fi'
                    }))
                    if (this.$refs.tabs) this.$refs.tabs.activeTab = 'chat'
                    this.$nextTick(() => this.scrollToBottom())
                } else {
                    this.addBotMessage(this.$t('ai_chat.welcome'))
                }
            }, () => { this.addBotMessage(this.$t('ai_chat.welcome')) })
        },
        deleteConversation(id) {
            if (!id) return
            this.conversations = this.conversations.filter(c => c.id !== id)
            if (this.conversationId === id) {
                this.conversationId = null
                this.messages = []
                this.addBotMessage(this.$t('ai_chat.welcome'))
            }
            this.restDelete(this.chatConversationUrl + '/' + encodeURIComponent(id), () => {}, () => { this.fetchConversations(false) })
        },
        addBotMessage(text, language) {
            this.messages.push({ role: 'assistant', content: text, language: language || 'fi' })
        },
        renderMarkdown(text) {
            if (!text) return ''
            return DOMPurify.sanitize(marked(text, { breaks: true }))
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const c = this.$refs.messagesContainer
                if (c) c.scrollTop = c.scrollHeight
            })
        },
        formatDate(dateStr) {
            if (!dateStr) return ''
            return new Date(dateStr).toLocaleDateString()
        }
    }
}
</script>

<style scoped>
/* Messages */
.chat-messages { flex: 1; overflow-y: auto; padding: 1em; background: #f8f8f8; }

/* History tab */
.sidebar-list { flex: 1; overflow-y: auto; background: #f8f8f8; }
.sidebar-empty { padding: 1em; color: #999; font-size: 0.85em; text-align: center; }
.sidebar-item {
    padding: 0.6em 0.8em;
    cursor: pointer;
    border-bottom: 1px solid #e8e8e8;
    transition: background 0.15s;
}
.sidebar-item:hover { background: #e4e6ea; }
.sidebar-item.active { background: #d4e4f7; }
.sidebar-item-title { font-size: 0.85em; color: #333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sidebar-item-meta { font-size: 0.75em; color: #999; margin-top: 2px; display: flex; justify-content: space-between; align-items: center; }
.delete-icon { color: #cc4444; font-size: 0.9em; opacity: 0; transition: opacity 0.15s; }
.sidebar-item:hover .delete-icon { opacity: 1; }

.message { margin-bottom: 0.8em; display: flex; }
.message.user { justify-content: flex-end; }
.message.assistant { justify-content: flex-start; }

.message-content {
    max-width: 92%; padding: 0.6em 1em; border-radius: 1em;
    word-wrap: break-word; font-size: 0.92em; line-height: 1.45;
}
.message.user .message-content { background: #7FC000; color: #fff; border-bottom-right-radius: 0.2em; }
.message.assistant .message-content { background: #fff; color: #404041; border: 1px solid #d9d9d9; border-bottom-left-radius: 0.2em; }

.typing-skeleton { min-width: 160px; display: flex; flex-direction: column; gap: 0.4em; }
.typing-skeleton .placeholder { height: 0.7em; border-radius: 4px; background-color: #e0e0e0; display: block; }

.faq-buttons { display: flex; flex-direction: column; gap: 0.8em; padding: 0.5em 0; }
.faq-button {
    background: #fff; border: none; color: #404041; border-radius: 1em;
    padding: 0.6em 1.2em; font-size: 0.85em; cursor: pointer;
    transition: background 0.15s; text-align: left;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.faq-button:hover { background: #f0f8e0; }

.chat-input {
    display: flex; padding: 0.8em; background: #fff;
    border-top: 1px solid #d9d9d9; flex-shrink: 0;
}
.chat-input input {
    flex: 1; padding: 0.6em; border: 1px solid #d9d9d9;
    border-radius: 0.3em; font-size: 0.92em; outline: none;
}
.chat-input input:focus { border-color: #7FC000; }

.send-button {
    margin-left: 0.5em; padding: 0.6em 1em; background: #7FC000;
    color: #fff; border: none; border-radius: 0.3em; cursor: pointer;
    transition: background 0.2s;
}
.send-button:hover:not(:disabled) { background: #669a01; }
.tab-bar-actions .action-icon { color: #666; }
</style>

<style>
/* Unscoped markdown styles */
.tool-panel .markdown-body h1,
.tool-panel .markdown-body h2,
.tool-panel .markdown-body h3,
.tool-panel .markdown-body h4,
.tool-panel .markdown-body h5,
.tool-panel .markdown-body h6 { margin: 0.75em 0 0.25em; font-weight: 700; }
.tool-panel .markdown-body h1 { font-size: 1.5em; }
.tool-panel .markdown-body h2 { font-size: 1.25em; }
.tool-panel .markdown-body h3 { font-size: 1.1em; }
.tool-panel .markdown-body h4 { font-size: 1em; }
.tool-panel .markdown-body h5 { font-size: 0.9em; }
.tool-panel .markdown-body h6 { font-size: 0.85em; }
.tool-panel .markdown-body p { margin: 0.5em 0; }
.tool-panel .markdown-body ul,
.tool-panel .markdown-body ol { margin: 0.5em 0; padding-left: 1.4em; }
.tool-panel .markdown-body li { margin-bottom: 0.25em; }
.tool-panel .markdown-body code { background: #f0f0f0; padding: 0.1em 0.3em; border-radius: 3px; font-size: 0.88em; }
.tool-panel .markdown-body pre { background: #f0f0f0; padding: 0.6em; border-radius: 4px; overflow-x: auto; margin: 0.6em 0; }
.tool-panel .markdown-body pre code { background: none; padding: 0; }
.tool-panel .markdown-body a { color: #0b4b8f; text-decoration: underline; }
.tool-panel .markdown-body strong { font-weight: 700; }
.tool-panel .markdown-body blockquote { border-left: 3px solid #d9d9d9; margin: 0.6em 0; padding-left: 0.8em; color: #666; }
.tool-panel .markdown-body table { border-collapse: collapse; margin: 0.6em 0; font-size: 0.88em; }
.tool-panel .markdown-body th,
.tool-panel .markdown-body td { border: 1px solid #d9d9d9; padding: 0.3em 0.5em; }
.tool-panel .markdown-body th { background: #f0f0f0; }
.tool-panel .markdown-body sub { font-size: 0.65em; color: #888; line-height: 1.4; display: inline-block; margin-top: 0.2em; }
</style>

<style scoped>
.translation-disclaimer { font-size: 0.75em !important; color: #888; line-height: 1.4; display: block !important; margin-top: 1.5em; padding-bottom: 1em; }
.disclaimer-icon { color: #7FC000; margin-right: 0.4em; }
</style>
